import { useEffect, useState } from "react";
import AdminHeader from "../../component/AdminHeader";
import ApiRequest from "../../helper/ApiRequest";
import { ApiEndpoints } from "../../helper/ApiEndpoints";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import CustomSelect from "../../component/CustomSelect";
import AdditionalSchemas, {
  normalizeAdditionalSchemas,
  validateOptionalSchemas,
} from "./AdditionalSchemas";
import {
  ogLocaleOptions,
  ogTypeOptions,
  buildPageSeoPayload,
  synchronizeSeoFields,
  updateSeoField,
} from "./SeoFieldOptions";

const seoEditEndpoint = ApiEndpoints.seo.seoEdit;
const seoUpdateEndpoint = ApiEndpoints.seo.seoUpdate;

export default function SeoEdit() {
  const { seo_id } = useParams();
  const [seoData, setSeoData] = useState({
    url: "",
    title: "",
    description: "",
    keywords: "",
    canonical_tag: "",
    faq_schema: "",
    og_locale: "en_IN",
    og_type: "website",
    og_title: "",
    og_description: "",
    og_url: "",
    og_image: "",
    twitter_title: "",
    twitter_description: "",
    twitter_image: "",
    article_schema: "",
    additional_schemas: [],
    seo_id,
  });

  const navigate = useNavigate();

  const handleChange = (e) => {
    const { name, value } = e.target;
    setSeoData((prevData) => ({
      ...updateSeoField(prevData, name, value),
      seo_id,
    }));
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await ApiRequest("GET", `${seoEditEndpoint}${seo_id}`);
        setSeoData(synchronizeSeoFields({
          ...response.seo,
          seo_id,
          og_locale: response.seo?.og_locale || "en_IN",
          og_type: response.seo?.og_type || "website",
          additional_schemas: normalizeAdditionalSchemas(response.seo?.additional_schemas),
        }));
      } catch (error) {
        console.error("Error fetching blog data:", error);
      }
    };

    fetchData();
  }, [seo_id]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    for (const key of ["url", "title", "description", "keywords"]) {
      if (!seoData[key] || seoData[key].trim() === "") {
        toast.error(`The field "${key}" is mandatory.`);
        return;
      }
    }

    try {
      validateOptionalSchemas(seoData);
    } catch (error) {
      toast.error("Please enter valid JSON in every non-empty schema field.");
      return;
    }

    try {
      const response = await ApiRequest("POST", seoUpdateEndpoint, buildPageSeoPayload(seoData));
      if (response.error === false) {
        toast.success(response.message);
        navigate("/seo");
      } else {
        console.error("Failed to update SEO:", response.message);
      }
    } catch (error) {
      console.error("Error updating SEO:", error);
    }
  };

  const pagename = {
    name: "SEO Management",
    user: "Admin",
    innerpage: "SEO Management / Edit SEO",
  };

  return (
    <>
      <AdminHeader pagename={pagename} />

      <div className="add-content-page">
        <form id="seoEdit" onSubmit={handleSubmit}>
          <div className="add-content-wrap">
            <div className="row">
              {[
                ["url", "URL", true],
                ["title", "Meta Title", true],
                ["description", "Meta Description", true],
                ["keywords", "Meta Keywords", true],
                ["canonical_tag", "Canonical Tag", false],
              ].map(([name, label, required]) => (
                <div className="col-lg-6" key={name}>
                  <div className="add-page-inputs">
                    <div className="label-wrap-add">
                      <label>{label}{required && <small>*</small>}</label>
                    </div>
                    <input type="text" name={name} value={seoData[name] || ""} onChange={handleChange} className="form-control" />
                  </div>
                </div>
              ))}

              <div className="col-lg-6">
                <div className="add-page-inputs">
                  <div className="label-wrap-add">
                    <label>OG Locale</label>
                  </div>
                  <CustomSelect
                    name="og_locale"
                    options={ogLocaleOptions.map(([value, label]) => ({ _id: value, name: `${label} (${value})` }))}
                    defaultValue={seoData.og_locale}
                    placeholder="Select OG Locale"
                    onChange={handleChange}
                  />
                </div>
              </div>

              <div className="col-lg-6">
                <div className="add-page-inputs">
                  <div className="label-wrap-add">
                    <label>OG Type</label>
                  </div>
                  <CustomSelect
                    name="og_type"
                    options={ogTypeOptions.map((type) => ({ _id: type, name: type }))}
                    defaultValue={seoData.og_type}
                    placeholder="Select OG Type"
                    onChange={handleChange}
                  />
                </div>
              </div>

              <div className="col-lg-6">
                <div className="add-page-inputs">
                  <div className="label-wrap-add">
                    <label>OG Image</label>
                  </div>
                  <input type="text" name="og_image" value={seoData.og_image || ""} onChange={handleChange} className="form-control" />
                </div>
              </div>

              {["og_title", "og_description", "og_url", "twitter_title", "twitter_description", "twitter_image"].map((field) => (
                <div className="col-lg-6" key={field}>
                  <div className="add-page-inputs">
                    <div className="label-wrap-add">
                      <label>{field.split("_").map((word) => word.charAt(0).toUpperCase() + word.slice(1)).join(" ")}</label>
                    </div>
                    <input type="text" name={field} value={seoData[field] || ""} className="form-control" disabled />
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="add-content-wrap">
            <div className="row">
              <div className="col-lg-12">
                <div className="add-page-inputs">
                  <div className="label-wrap-add">
                    <label> FAQ Schema</label>
                  </div>
                  <textarea name="faq_schema" value={seoData.faq_schema || ""} onChange={handleChange} className="form-control" rows="5" style={{ height: "auto" }}></textarea>
                </div>
              </div>
            </div>
          </div>

          <div className="add-content-wrap">
            <div className="row">
              <div className="col-lg-12">
                <div className="add-page-inputs">
                  <div className="label-wrap-add">
                    <label>Article Schema</label>
                  </div>
                  <textarea name="article_schema" value={seoData.article_schema || ""} onChange={handleChange} className="form-control" rows="5" style={{ height: "auto" }}></textarea>
                </div>
              </div>
            </div>
          </div>

          <AdditionalSchemas
            value={seoData.additional_schemas}
            onChange={(additional_schemas) =>
              setSeoData((current) => ({ ...current, seo_id, additional_schemas }))
            }
          />
        </form>

        <button type="submit" form="seoEdit" className="default-btn">Save <span></span></button>
      </div>
    </>
  );
}
